import { useState } from 'react';
import type { TierConfig, TierValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';
import { FootnoteNote } from './FootnoteNote';

interface TierPickerProps {
  field: Pick<Field, 'label' | 'allowFootnote'>;
  config: TierConfig;
  value: TierValue | null;
  onCommit: (value: TierValue | null) => void;
  disabled?: boolean;
}

/** Segmented SSS…D buttons + a "clear" button, plus footnote/note when the field allows it. */
export function TierPicker({ field, config, value, onCommit, disabled = false }: TierPickerProps) {
  const [note, setNote] = useState(value?.note ?? '');

  function selectTier(tier: string) {
    if (disabled) {
      return;
    }
    onCommit({ type: 'TIER', tier, footnote: value?.footnote ?? false, note: value?.note ?? null });
  }

  function clear() {
    if (disabled) {
      return;
    }
    onCommit(null);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${field.label} tier`}>
        {config.tiers.map((tier) => (
          <button
            key={tier}
            type="button"
            disabled={disabled}
            aria-pressed={value?.tier === tier}
            onClick={() => selectTier(tier)}
            className={[
              'display min-h-tap min-w-tap rounded-md border px-3 text-2xl transition-[color,border-color,background-color,transform] duration-75 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 disabled:cursor-not-allowed disabled:opacity-50',
              value?.tier === tier
                ? 'border-accent bg-accent-dim text-accent'
                : 'border-border-strong text-text-muted hover:border-accent-border hover:text-text',
            ].join(' ')}
          >
            {tier}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={clear}
          className="min-h-tap min-w-tap rounded-md border border-border-strong px-3 text-sm text-text-faint transition-colors hover:border-signal hover:text-signal disabled:cursor-not-allowed disabled:opacity-50"
        >
          — clear
        </button>
      </div>
      {field.allowFootnote && (
        <FootnoteNote
          footnote={value?.footnote ?? false}
          note={note}
          disabled={disabled || !value}
          onFootnoteChange={(footnote) => value && onCommit({ ...value, footnote })}
          onNoteChange={setNote}
          onNoteBlur={() => value && onCommit({ ...value, note: note.trim() === '' ? null : note })}
        />
      )}
    </div>
  );
}
